import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Platform, Animated, Easing, Image, Dimensions } from 'react-native';
import { BlurView } from 'expo-blur';
import LoadingSpinner from '../components/LoadingSpinner';
import { LinearGradient } from 'expo-linear-gradient';
import Svg, { Path, LinearGradient as SvgLinearGradient, Defs, Stop, G, ClipPath, Rect } from 'react-native-svg';
import { useNavigation } from '../context/NavigationContext';
import { Input } from './RegisterScreen';
import { useAuth } from '../context/AuthContext';
import GradientText from '../components/GradientText';

const { width: screenWidth } = Dimensions.get('window');
const isMobile = screenWidth < 768;

const LogoIcon = () => (
  <View style={styles.logoContainer}>
    <Image source={require('../assets/icon.png')} style={{ width: 48, height: 48, borderRadius: 10 }} resizeMode="contain" />
  </View>
);

const GoogleIcon = () => (
  <Svg width={20} height={20} viewBox="0 0 21 20" fill="none">
    <Defs>
      <ClipPath id="clip0_384_103">
        <Rect width="20" height="20" fill="white" transform="translate(0.738525)" />
      </ClipPath>
    </Defs>
    <G clipPath="url(#clip0_384_103)">
      <Path d="M20.728 10.1871C20.728 9.36767 20.6599 8.76973 20.5127 8.14966H10.9377V11.848H16.558C16.4448 12.7671 15.8329 14.1512 14.4731 15.0813L14.454 15.2051L17.4814 17.4969L17.6912 17.5174C19.6175 15.7789 20.728 13.221 20.728 10.1871Z" fill="#1769F2" />
      <Path d="M10.9378 19.9313C13.6912 19.9313 16.0028 19.0454 17.6912 17.5174L14.4731 15.0813C13.612 15.6682 12.4561 16.0779 10.9378 16.0779C8.24095 16.0779 5.95204 14.3395 5.13611 11.9366L5.01652 11.9466L1.86856 14.3273L1.82739 14.4391C3.50441 17.6945 6.94913 19.9313 10.9378 19.9313Z" fill="#298642" />
      <Path d="M5.136 11.9367C4.92071 11.3166 4.79611 10.6522 4.79611 9.96571C4.79611 9.27915 4.92071 8.61479 5.12467 7.99472L5.11897 7.86266L1.93157 5.44373L1.82728 5.4922C1.1361 6.84311 0.739502 8.36014 0.739502 9.96571C0.739502 11.5713 1.1361 13.0882 1.82728 14.4391L5.136 11.9367Z" fill="#967002" />
      <Path d="M10.9378 3.85336C12.8527 3.85336 14.1445 4.66168 14.881 5.33718L17.7592 2.59107C15.9916 0.985496 13.6912 0 10.9378 0C6.94913 0 3.50441 2.23672 1.82739 5.49214L5.12479 7.99466C5.95205 5.59183 8.24095 3.85336 10.9378 3.85336Z" fill="#E92F20" />
    </G>
  </Svg>
);


export default function SignInScreen() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState(null);
  const [isExiting, setIsExiting] = useState(false);

  // Animation values
  const pageOpacity = useRef(new Animated.Value(0)).current;

  const { navigateTo, handleLoginRedirect } = useNavigation();
  const { signIn, signInWithGoogleAuth } = useAuth();

  // Page load animation
  useEffect(() => {
    Animated.timing(pageOpacity, {
      toValue: 1,
      duration: 1000,
      easing: Easing.inOut(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, []);

  // Exit animation function
  const exitAnimation = (callback) => {
    setIsExiting(true);
    Animated.timing(pageOpacity, {
      toValue: 0,
      duration: 600,
      easing: Easing.in(Easing.cubic),
      useNativeDriver: true,
    }).start(() => {
      if (callback) callback();
    });
  };

  const handleSignIn = async () => {
    if (emailLoading || googleLoading || isExiting) return;
    
    setEmailLoading(true);
    setStatus('');
    setStatusType(null);
    
    try {
      const { user, error } = await signIn(email, password);
      
      if (error) {
        setStatus(error);
        setStatusType('error');
        setEmailLoading(false);
      } else {
        // Success - trigger exit animation then navigate
        exitAnimation(() => {
          handleLoginRedirect();
        });
      }
    } catch (error) {
      console.error('Email Sign-In Error:', error);
      setStatus('An unexpected error occurred. Please try again.');
      setStatusType('error');
      setEmailLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    if (emailLoading || googleLoading || isExiting) return;
    
    setGoogleLoading(true);
    setStatus('');
    setStatusType(null);
    
    try {
      const { user, error } = await signInWithGoogleAuth();
      
      if (error) {
        setStatus(error);
        setStatusType('error');
        setGoogleLoading(false);
      } else {
        // Success - trigger exit animation then navigate
        exitAnimation(() => {
          handleLoginRedirect();
        });
      }
    } catch (error) {
      console.error('Google Sign-In Error:', error);
      setStatus('An unexpected error occurred. Please try again.');
      setStatusType('error');
      setGoogleLoading(false);
    }
  };

  const handleForgotPassword = () => {
    exitAnimation(() => {
      navigateTo('ForgotPassword');
    });
  };

  const goToRegister = () => {
    if (Platform.OS === 'web') {
      try {
        window.history.pushState({}, '', '/register');
        window.dispatchEvent(new Event('app:navigate'));
      } catch {
        window.location.assign('/register');
      }
    }
  };

  return (
    <View style={styles.container}>
      <Animated.View style={[
        styles.mainContent,
        {
          opacity: pageOpacity,
        }
      ]}>
        <View style={styles.headerSection}>
          <LogoIcon />
          <Text style={styles.brandTitle}>BET OPTIMIZED</Text>
        </View>

        <View style={styles.formContainer}>
          <BlurView intensity={20} tint="dark" style={styles.formCard}>
            <View style={styles.formContent}>
              <GradientText style={styles.signInTitle}>Sign In</GradientText>

              <View style={styles.formSection}>
                <Input placeholder="Enter your email" disabled={emailLoading || googleLoading || isExiting} value={email} onChangeText={setEmail} keyboardType="email-address" autoCapitalize="none" />
                <Input placeholder="Enter your password" disabled={emailLoading || googleLoading || isExiting} value={password} onChangeText={setPassword} keyboardType="email-address" autoCapitalize="none" secureTextEntry={true} />
                <TouchableOpacity 
                  style={[styles.signInButton, (emailLoading || googleLoading || isExiting) && styles.buttonDisabled]} 
                  onPress={handleSignIn}
                  disabled={emailLoading || googleLoading || isExiting}
                >
                  <LinearGradient
                    colors={['rgba(0, 0, 0, 0.16)', 'rgba(116, 132, 154, 0.16)']}
                    style={styles.buttonGradient}
                  >
                    {emailLoading ? (
                      <View style={styles.loadingContainer}>
                        <LoadingSpinner size={16} color="rgba(255, 255, 255, 0.9)" />
                        <GradientText style={styles.signInButtonText}>Signing In...</GradientText>
                      </View>
                    ) : (
                      <GradientText style={styles.signInButtonText}>Sign In</GradientText>
                    )}
                  </LinearGradient>
                </TouchableOpacity>
              </View>

              <View style={styles.dividerSection}>
                <View style={styles.dividerLine} />
                <Text style={styles.dividerText}>or continue with</Text>
                <View style={styles.dividerLine} />
              </View>

              <TouchableOpacity 
                style={[styles.googleButton, (emailLoading || googleLoading || isExiting) && styles.buttonDisabled]} 
                onPress={handleGoogleSignIn}
                disabled={emailLoading || googleLoading || isExiting}
              >
                <LinearGradient
                  colors={['rgba(0, 0, 0, 0.16)', 'rgba(116, 132, 154, 0.16)']}
                  style={styles.googleButtonGradient}
                >
                  {googleLoading ? (
                    <View style={styles.loadingContainer}>
                      <LoadingSpinner size={16} color="rgba(255, 255, 255, 0.9)" />
                      <Text style={styles.googleLoadingText}>Signing in with Google...</Text>
                    </View>
                  ) : (
                    <>
                      <GoogleIcon />
                      <Text style={styles.googleButtonText}>Google</Text>
                    </>
                  )}
                </LinearGradient>
              </TouchableOpacity>

              <TouchableOpacity onPress={handleForgotPassword} disabled={emailLoading || googleLoading || isExiting}>
                <Text style={[styles.forgotPasswordText, (emailLoading || googleLoading || isExiting) && styles.textDisabled]}>Forgot Password?</Text>
              </TouchableOpacity>
              {status ? (
                <Text style={[
                  styles.statusText,
                  statusType === 'success' ? styles.statusSuccess : null,
                  statusType === 'error' ? styles.statusError : null,
                ]}>{status}</Text>
              ) : null}
              <TouchableOpacity disabled={emailLoading || googleLoading || isExiting} onPress={() => navigateTo('Register')}>
                <Text style={[styles.forgotPasswordText, (emailLoading || googleLoading || isExiting) && styles.textDisabled]}>Don't have an account? Create one</Text>
              </TouchableOpacity>
            </View>
          </BlurView>
        </View>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#131313',
    borderWidth: 2,
    borderColor: '#000',
  },
  mainContent: {
    flex: 1,
    paddingTop: isMobile ? 16 : (Platform.OS === 'web' ? 48 : 20),
    paddingHorizontal: isMobile ? 16 : (Platform.OS === 'web' ? 48 : 20),
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerSection: {
    alignItems: 'center',
    marginBottom: isMobile ? 20 : 32,
    gap: isMobile ? 12 : 16,
  },
  logoContainer: {
    width: isMobile ? 48 : 64,
    height: isMobile ? 48 : 64,
    justifyContent: 'center',
    alignItems: 'center',
  },
  brandTitle: {
    fontFamily: Platform.OS === 'web' ? 'ClimateCrisis' : 'ClimateCrisis',
    fontSize: isMobile ? 24 : (Platform.OS === 'web' ? 64 : 32),
    fontWeight: '400',
    lineHeight: isMobile ? 28 : (Platform.OS === 'web' ? 64 : 36),
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.85)',
  },
  formContainer: {
    width: '100%',
    maxWidth: 600,
    minHeight: isMobile ? 320 : 400,
    justifyContent: 'center',
    alignItems: 'center',
  },
  formCard: {
    width: '100%',
    height: '100%',
    padding: isMobile ? 20 : 48,
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  formContent: {
    width: '100%',
    gap: isMobile ? 20 : 32,
    alignItems: 'center',
  },
  signInTitle: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: isMobile ? 22 : 28,
    fontWeight: '500',
    lineHeight: isMobile ? 26 : 32,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.85)',
  },
  formSection: {
    width: '100%',
    gap: isMobile ? 12 : 16,
  },
  inputContainer: {
    width: '100%',
    padding: 12,
    borderRadius: 4,
    backgroundColor: 'rgba(0, 0, 0, 0.00)',
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 4,
  },
  emailInput: {
    fontFamily: Platform.OS === 'web' ? 'Slate, -apple-system, Roboto, Helvetica, sans-serif' : 'System',
    fontSize: 15,
    fontWeight: '400',
    lineHeight: 20,
    color: '#fff',
    padding: 0,
  },
  signInButton: {
    width: '100%',
    borderRadius: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 4,
  },
  buttonGradient: {
    padding: 8,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  signInButtonText: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: 15,
    fontWeight: '500',
    lineHeight: 20,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  dividerSection: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%',
    gap: 8,
  },
  dividerLine: {
    flex: 1,
    height: 1,
    backgroundColor: 'rgba(112, 112, 112, 0.5)',
  },
  dividerText: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: 13,
    fontWeight: '400',
    color: 'rgba(255, 255, 255, 0.75)',
  },
  googleButton: {
    width: '100%',
    borderRadius: 4,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 4,
  },
  googleButtonGradient: {
    padding: 8,
    borderRadius: 4,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  googleButtonText: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: 15,
    fontWeight: '500',
    lineHeight: 20,
    color: 'rgba(255, 255, 255, 0.9)',
  },
  googleLoadingText: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: 15,
    fontWeight: '500',
    lineHeight: 20,
    color: 'rgba(255, 255, 255, 0.9)',
    marginLeft: 8,
  },
  forgotPasswordText: {
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: 13,
    fontWeight: '400',
    lineHeight: 16,
    color: 'rgba(255, 255, 255, 0.75)',
  },
  statusText: {
    marginTop: 8,
    fontFamily: Platform.OS === 'web' ? "Slate, -apple-system, system-ui, 'Segoe UI', Roboto, Inter, 'Helvetica Neue', Arial, sans-serif" : 'System',
    fontSize: 13,
    lineHeight: 16,
    color: 'rgba(255,255,255,0.85)'
  },
  statusSuccess: {
    color: '#28C76F'
  },
  statusError: {
    color: '#EA5455'
  },
  loadingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  textDisabled: {
    opacity: 0.5,
  },
});
